'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import { useState } from 'react';

import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { CardShared } from '@/app/components/card/shared';
import { IconArrowBack } from '@/app/components/icons/ui-actions/icon-arrow-back';
import { IconArrowForward } from '@/app/components/icons/ui-actions/icon-arrow-forward';
import { PaginationDash } from '@/app/components/navigation/pagination';
import styles from '@/app/components/sections/content-hub/styles.module.css';
import {
  CONTENT_HUB_DATA_GA,
  CONTENT_HUB_DATA_TEST,
} from '@/app/components/sections/content-hub/tracking';
import type { ContentHubEntry } from '@/app/components/sections/content-hub/types';
import { parseExpertListItems } from '@/app/components/sections/content-hub/utils';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { usePrevNextSlider } from '@/app/components/sliders/hooks/usePrevNextSlider';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

interface ExpertRelatedProps {
  category: string;
  items: ContentHubEntry[];
}

export const ExpertRelated = ({ category, items = [] }: ExpertRelatedProps) => {
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextSlider(emblaApi);
  const { currentPage, onPageChange, scrollSnaps } =
    usePaginationSlider(emblaApi);

  const listItems = parseExpertListItems(category, items);
  return (
    <>
      <div className="mb-6 flex items-center justify-between sm:mb-10">
        <h2
          {...createDataAttributes({
            test: 'related-title',
          })}
          className="font-display text-h2"
        >
          บทความที่เกี่ยวข้อง
        </h2>
        {listItems.length > 1 && (!prevBtnDisabled || !nextBtnDisabled) && (
          <ButtonGroup
            className="hidden md:flex"
            {...createDataAttributes({
              test: 'related-pagination',
            })}
          >
            <Button
              aria-label="Previous"
              iconBefore={<IconArrowBack />}
              color="black"
              variant="primary"
              disabled={prevBtnDisabled}
              onClick={onPrevButtonClick}
              {...createDataAttributes({
                test: 'related-prev',
              })}
              {...createDataGA({
                click: true,
                eventName: CONTENT_HUB_DATA_GA.relatedItem,
                value: 'prev-btn',
              })}
            />
            <Button
              aria-label="Next"
              iconBefore={<IconArrowForward />}
              color="black"
              variant="primary"
              disabled={nextBtnDisabled}
              onClick={onNextButtonClick}
              {...createDataAttributes({
                test: 'related-next',
              })}
              {...createDataGA({
                click: true,
                eventName: CONTENT_HUB_DATA_GA.relatedItem,
                value: 'next-btn',
              })}
            />
          </ButtonGroup>
        )}
      </div>
      <div
        {...createDataAttributes({
          test: 'related-items',
        })}
      >
        <Sliders
          onEmblaInit={setEmblaApi}
          className="xl:overflow-hidden"
          containerClassName="gap-x-4 xs:gap-x-6 sm:gap-x-8"
          hidden={false}
        >
          {listItems.map((item, index) => (
            <div
              key={index}
              className={cn('shrink-0', styles.relatedSlide)}
              {...createDataAttributes({
                test: `item-${index}`,
              })}
            >
              <CardShared
                {...item}
                image={{
                  ...item.image,
                  sizes:
                    '(min-width: 1280px) 360px, (min-width: 768px) 30vw, 90vw',
                }}
                {...createDataAttributes({
                  test: `${CONTENT_HUB_DATA_TEST.relatedItem}-${index}`,
                })}
                dataGA={{
                  eventName: CONTENT_HUB_DATA_GA.relatedItem,
                  click: true,
                  text: item.title,
                  url: item.href,
                }}
              />
            </div>
          ))}
        </Sliders>
      </div>
      {scrollSnaps.length > 1 && (
        <PaginationDash
          theme="dark"
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={scrollSnaps.length}
          wrapperClassName="mt-6 justify-center md:hidden"
        />
      )}
    </>
  );
};
